import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  animated,
  useTransition,
  easings,
  type SpringValues,
} from '@react-spring/web';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';

const yTranslation = 60;

interface NumberCounterProps {
  value: number;
}

export const NumberCounter = ({ value }: NumberCounterProps) => {
  const [numberWidth, setNumberWidth] = useState(0);
  const numberRef = useRef<HTMLSpanElement | undefined>(undefined);

  const transitions = useTransition(value, {
    from: { y: yTranslation },
    enter: { y: 0 },
    leave: { y: -yTranslation },
    config: { duration: 500, easing: easings.easeOutBack },
  });

  useLayoutEffect(
    () => {
      if (numberRef.current) {
        const width = numberRef.current.offsetWidth;
        const marginRight = 8;
        setNumberWidth(width + marginRight);
      }
    },
    /**
     * We're updating `numberWidth` when `value` changes, because the this will
     * update the layout before re-rendering. If we were to use
     * `numberRef.current?offsetWidth` directly, the rendered text would have
     * the improper `numberWidth` for a fraction of time.
     */
    [value],
  );

  return (
    // Needed so that the number can be aligned (baseline) with other text
    <Box display="inline-block" width={`${numberWidth}px`}>
      {/* Relative container to enable animation:
      during the transition between two values both of them are present in the DOM 
      and they both need to be positioned in the same place, thus absolute positioning of animated children.
      One will animate out, one will animate in, both from the same reference point.
      */}
      <Text
        position="relative"
        fontWeight="semiBold"
        color="text"
        fontFamily="heading"
        fontSize={9}
        lineHeight="none"
        // Doesn't really have content, but it is needed for this element to have height. Otherwise it will be 0 becuase it's child is absolutely positioned.
        extend={() => ({
          '&::before': {
            content: '""',
            display: 'inline-block',
          },
        })}
      >
        {transitions((style, item) => (
          <Box
            overflow="hidden"
            display="inline-flex" // needs to be both, inline for the text alignment (Safari and Firefox) and flex for the absolute positioning and the animation
            flexDirection="column"
            position="absolute"
          >
            <Text
              ref={numberRef}
              as={animated.span}
              fontWeight="semiBold"
              color="text"
              fontFamily="heading"
              fontSize={9}
              lineHeight="none"
              style={style as SpringValues}
            >
              {item}
            </Text>
          </Box>
        ))}
      </Text>
    </Box>
  );
};
